import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Filter, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';

import { useCatalogItemsApi , useCatalogItemActions , useCatalogLookups } from '../../services/catalog';
import { useCategoriesApi } from '../../services/categories';
import { useSuppliersApi } from '../../services/suppliers';
import { useAppSettings } from '../../hook/useAppSettings';
import type { CatalogItem , CatalogPagination , CatalogItemFilters} from '../../types/catalog';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { CatalogItemDialog, type CatalogItemForm } from '../catalog/CatalogItemDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

function useDebounce<T>(value: T, delay: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const INITIAL_ADVANCED_FILTERS = { category: 'all', minUnitCost: '', maxUnitCost: '', minPvp: '', maxPvp: '' };
const LOOKUP_PAGE_SIZE = 20;
const parseInputDecimal = (value: string | number | null | undefined) => {
  if (value == null) return 0;
  const normalized = String(value).trim().replace(',', '.');
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

export function Items() {
  const { currencySymbol } = useAppSettings();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const initialSupplier = useMemo(() => {
    const fromState = (location.state as { supplier?: string } | null)?.supplier;
    const fromQuery = searchParams.get('supplier');
    return (fromQuery ?? fromState ?? '').trim() || null;
  }, [location.state, searchParams]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);
  const [supplierFilter, setSupplierFilter] = useState<'all' | string>(() => initialSupplier ?? 'all');
  const [advancedFilters, setAdvancedFilters] = useState(INITIAL_ADVANCED_FILTERS);
  const [appliedAdvancedFilters, setAppliedAdvancedFilters] = useState(INITIAL_ADVANCED_FILTERS);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create'|'edit'>('create');
  const [selectedItem, setSelectedItem] = useState<CatalogItem | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<CatalogItem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [pageInput, setPageInput] = useState('1');
  const [supplierLookupQuery, setSupplierLookupQuery] = useState('');
  const [categoryLookupQuery, setCategoryLookupQuery] = useState('');
  const debouncedSupplierLookupQuery = useDebounce(supplierLookupQuery, 300);
  const debouncedCategoryLookupQuery = useDebounce(categoryLookupQuery, 300);
  const { updateCatalogItem, deleteCatalogItem, createCatalogItem} = useCatalogItemActions();

  const {
    data: items,
    loading,
    error,
    pagination,
    setFilters,
    setPagination,
    goToPage,
    refresh,
  } = useCatalogItemsApi(
    {
      search: undefined,
      supplier: initialSupplier ?? undefined,
      category: undefined,
      min_unit_cost: undefined,
      max_unit_cost: undefined,
      min_pvp: undefined,
      max_pvp: undefined,
    },
    { page: 1, page_size: 50 },
  );
  const { lookups, loading: lookupsLoading } = useCatalogLookups();
  const {
    data: supplierLookupItems,
    loading: supplierLookupLoading,
    setFilters: setSupplierLookupFilters,
    setPagination: setSupplierLookupPagination,
  } = useSuppliersApi({ active: true }, { page: 1, page_size: LOOKUP_PAGE_SIZE });
  const {
    categories: categoryLookupItems,
    loading: categoryLookupLoading,
    refresh: refreshCategoryLookup,
  } = useCategoriesApi({ active: true, limit: LOOKUP_PAGE_SIZE });

  const supplierOptions = useMemo(() => {
    const base = supplierLookupItems.map((supplier) => ({
      label: supplier.name,
      value: supplier.id,
      meta: {
        desvio_comercial_pct: String(supplier.desvio_comercial_pct ?? '0'),
        beneficio_comercial_pct: String(supplier.beneficio_comercial_pct ?? '0'),
      },
    }));
    if (dialogMode === 'edit' && selectedItem && !base.some((option) => option.value === selectedItem.supplier_id)) {
      return [
        {
          label: selectedItem.supplier,
          value: selectedItem.supplier_id,
          meta: {
            desvio_comercial_pct: String(selectedItem.desvio_comercial_pct ?? '0'),
            beneficio_comercial_pct: String(selectedItem.beneficio_comercial_pct ?? '0'),
          },
        },
        ...base,
      ];
    }
    return base;
  }, [dialogMode, selectedItem, supplierLookupItems]);

  const categoryOptions = useMemo(() => {
    const base = categoryLookupItems.map((category) => ({ label: category.name, value: category.id }));
    if (dialogMode === 'edit' && selectedItem && !base.some((option) => option.value === selectedItem.category_id)) {
      return [{ label: selectedItem.category, value: selectedItem.category_id }, ...base];
    }
    return base;
  }, [categoryLookupItems, dialogMode, selectedItem]);

  useEffect(() => {
    if (!initialSupplier) return;
    setSupplierFilter((prev) => (prev === initialSupplier ? prev : initialSupplier));
  }, [initialSupplier]);

  useEffect(() => {
    if (!dialogOpen) return;
    const search = debouncedSupplierLookupQuery.trim() || undefined;
    setSupplierLookupFilters((prev) => {
      const next = { ...prev, search, active: true };
      const same = prev.search === next.search && prev.active === next.active;
      return same ? prev : next;
    });
    setSupplierLookupPagination((prev) =>
      prev.page === 1 && prev.page_size === LOOKUP_PAGE_SIZE
        ? prev
        : { ...prev, page: 1, page_size: LOOKUP_PAGE_SIZE },
    );
  }, [
    debouncedSupplierLookupQuery,
    dialogOpen,
    setSupplierLookupFilters,
    setSupplierLookupPagination,
  ]);

  useEffect(() => {
    if (!dialogOpen) return;
    void refreshCategoryLookup({
      search: debouncedCategoryLookupQuery.trim() || undefined,
      active: true,
      limit: LOOKUP_PAGE_SIZE,
    });
  }, [debouncedCategoryLookupQuery, dialogOpen, refreshCategoryLookup]);

  const applyFiltersToApi = useCallback(
  (overrides?: Partial<typeof appliedAdvancedFilters>) => {
    const effective = { ...appliedAdvancedFilters, ...(overrides ?? {}) };
    const toNumber = (value: string) => (value.trim() ? Number(value) : undefined);

    const nextFilters: CatalogItemFilters = {
      search: debouncedSearch.trim() || undefined,
      supplier: supplierFilter === 'all' ? undefined : supplierFilter,
      category: effective.category === 'all' ? undefined : effective.category,
      min_unit_cost: toNumber(effective.minUnitCost),
      max_unit_cost: toNumber(effective.maxUnitCost),
      min_pvp: toNumber(effective.minPvp),
      max_pvp: toNumber(effective.maxPvp),
    };

    setFilters((prev) => {
      const same = ['search', 'supplier', 'category', 'min_unit_cost', 'max_unit_cost', 'min_pvp', 'max_pvp']
        .every((key) => prev[key as keyof CatalogItemFilters] === nextFilters[key as keyof CatalogItemFilters]);
      return same ? prev : nextFilters;
    });
    setPagination((prev: CatalogPagination) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  },
  [appliedAdvancedFilters, debouncedSearch, supplierFilter, setFilters, setPagination],
);

  useEffect(() => {
    applyFiltersToApi();
  }, [applyFiltersToApi]);


  const handleApplyAdvancedFilters = () => {
  const { minUnitCost, maxUnitCost, minPvp, maxPvp } = advancedFilters;
  const minCost = minUnitCost.trim() ? Number(minUnitCost) : undefined;
  const maxCost = maxUnitCost.trim() ? Number(maxUnitCost) : undefined;
  const minPvpVal = minPvp.trim() ? Number(minPvp) : undefined;
  const maxPvpVal = maxPvp.trim() ? Number(maxPvp) : undefined;

  if ((minCost && Number.isNaN(minCost)) || (maxCost && Number.isNaN(maxCost))) {
    toast.error('Introduce números válidos para costo.');
    return;
  }
  if ((minPvpVal && Number.isNaN(minPvpVal)) || (maxPvpVal && Number.isNaN(maxPvpVal))) {
    toast.error('Introduce números válidos para PVP.');
    return;
  }
  if (minCost !== undefined && maxCost !== undefined && minCost > maxCost) {
    toast.error('El costo mínimo no puede ser mayor que el máximo.');
    return;
  }
  if (minPvpVal !== undefined && maxPvpVal !== undefined && minPvpVal > maxPvpVal) {
    toast.error('El PVP mínimo no puede ser mayor que el máximo.');
    return;
  }

  setAppliedAdvancedFilters(advancedFilters);
  applyFiltersToApi(advancedFilters);
};

const handleResetFilters = () => {
  setSearchTerm('');
  setSupplierFilter('all');
  setAdvancedFilters(INITIAL_ADVANCED_FILTERS);
  setAppliedAdvancedFilters(INITIAL_ADVANCED_FILTERS);
  applyFiltersToApi(INITIAL_ADVANCED_FILTERS);
};

  const totalPages = Math.max(
    1,
    pagination.total_pages ?? Math.ceil((pagination.total ?? (items as CatalogItem[]).length) / pagination.page_size),
  );

  useEffect(() => {
    setPageInput(String(pagination.page));
  }, [pagination.page]);

  const commitPageInput = () => {
    const parsed = Number(pageInput);
    if (!Number.isInteger(parsed)) {
      setPageInput(String(pagination.page));
      return;
    }
    const nextPage = Math.min(totalPages, Math.max(1, parsed));
    if (nextPage !== pagination.page) void goToPage(nextPage);
  };

  const handleSave = async (values: CatalogItemForm) => {
    setSubmitting(true);
    const unitCost = parseInputDecimal(values.unitCost);
    const desvioComercialPct = parseInputDecimal(values.desvioComercialPct);
    const beneficioComercialPct = parseInputDecimal(values.beneficioComercialPct);
    const pvp = unitCost * (1 + beneficioComercialPct / 100);
    const supplierId = values.supplierId.trim();
    const categoryId = values.categoryId.trim();
    const cost = unitCost * (1 - desvioComercialPct / 100);

    if (!supplierId) {
      toast.error('Selecciona un proveedor existente para guardar el item.');
      setSubmitting(false);
      return;
    }
    if (!categoryId) {
      toast.error('Selecciona una categoría existente para guardar el item.');
      setSubmitting(false);
      return;
    }
    if (desvioComercialPct < 0 || desvioComercialPct > 100) {
      toast.error('El desvío comercial debe estar entre 0 y 100.');
      setSubmitting(false);
      return;
    }
    if (beneficioComercialPct < 0 || beneficioComercialPct > 100) {
      toast.error('El beneficio comercial debe estar entre 0 y 100.');
      setSubmitting(false);
      return;
    }
    if (pvp < cost) {
      toast.error('El PVP no puede ser menor que el costo.');
      setSubmitting(false);
      return;
    }

    const payload = {
      ref: values.ref.trim(),
      supplier: values.supplierName.trim(),
      supplier_id: supplierId,
      description: values.description.trim(),
      category_id: categoryId,
      unit_cost: unitCost.toFixed(2),
      pvp: pvp.toFixed(2),
      desvio_comercial_pct: desvioComercialPct.toFixed(2),
      beneficio_comercial_pct: beneficioComercialPct.toFixed(2),
    };

    try {
      if (dialogMode === 'edit' && selectedItem) {
        await updateCatalogItem(selectedItem.id, payload);
      } else {
        await createCatalogItem(payload);
      }
      toast.success('Item guardado');
      await refresh();
      setDialogOpen(false);
    } catch {
      toast.error('No pudimos guardar el item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSupplierSearch = useCallback((query: string) => {
    setSupplierLookupQuery(query);
  }, []);

  const handleCategorySearch = useCallback((query: string) => {
    setCategoryLookupQuery(query);
  }, []);

  const handleOpenCreateDialog = useCallback(() => {
    setSelectedItem(null);
    setDialogMode('create');
    setSupplierLookupQuery('');
    setCategoryLookupQuery('');
    setDialogOpen(true);
  }, []);

  const handleOpenEditDialog = useCallback((item: CatalogItem) => {
    setSelectedItem(item);
    setDialogMode('edit');
    setSupplierLookupQuery(item.supplier);
    setCategoryLookupQuery(item.category);
    setDialogOpen(true);
  }, []);

  const dialogInitialValues = useMemo<Partial<CatalogItemForm> | undefined>(() => {
    if (!selectedItem) return undefined;
    return {
      ref: selectedItem.ref,
      supplierName: selectedItem.supplier,
      supplierId: selectedItem.supplier_id,
      description: selectedItem.description,
      categoryName: selectedItem.category,
      categoryId: selectedItem.category_id,
      unitCost: String(selectedItem.unit_cost),
      desvioComercialPct: String(selectedItem.desvio_comercial_pct ?? '0'),
      beneficioComercialPct: String(selectedItem.beneficio_comercial_pct ?? '0'),
      customDesvioComercial: true,
      customBeneficioComercial: true,
    };
  }, [selectedItem]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl text-gray-900 mb-2">Gestión de Items/Muebles</h1>
          <p className="text-gray-600">Catálogo completo de productos y muebles</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleOpenCreateDialog}>
          <Plus className="w-4 h-4 mr-2" />
          Crear Item
        </Button>
      </div>

      <Card className="shadow-sm">
  <CardContent className="p-4">
    <div className="flex flex-col sm:flex-row gap-4">
      <div className="flex-1">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Buscar por descripción o referencia"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>
      <div className="flex gap-2">
        <Select
          value={supplierFilter}
          onValueChange={(value) => setSupplierFilter(value)}
          disabled={lookupsLoading}
        >
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Proveedor" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los proveedores</SelectItem>
            {lookups.suppliers.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => setShowAdvancedFilters((v) => !v)}>
          <Filter className="w-4 h-4 mr-2" />
          {showAdvancedFilters ? 'Ocultar filtros' : 'Más filtros'}
        </Button>
      </div>
    </div>

    {showAdvancedFilters && (
      <div className="mt-4 border-top border-gray-200 pt-4">
        <div className="grid gap-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Categoría</label>
              <Select
                value={advancedFilters.category}
                onValueChange={(value) => setAdvancedFilters((prev) => ({ ...prev, category: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todas las categorías" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {lookups.categories.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Costo mínimo ({currencySymbol})</label>
              <Input
                type="number"
                min="0"
                value={advancedFilters.minUnitCost}
                onChange={(e) => setAdvancedFilters((prev) => ({ ...prev, minUnitCost: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Costo máximo ({currencySymbol})</label>
              <Input
                type="number"
                min="0"
                value={advancedFilters.maxUnitCost}
                onChange={(e) => setAdvancedFilters((prev) => ({ ...prev, maxUnitCost: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">PVP mínimo ({currencySymbol})</label>
              <Input
                type="number"
                min="0"
                value={advancedFilters.minPvp}
                onChange={(e) => setAdvancedFilters((prev) => ({ ...prev, minPvp: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">PVP máximo ({currencySymbol})</label>
              <Input
                type="number"
                min="0"
                value={advancedFilters.maxPvp}
                onChange={(e) => setAdvancedFilters((prev) => ({ ...prev, maxPvp: e.target.value }))}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={handleResetFilters} disabled={loading}>
              Limpiar filtros
            </Button>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={handleApplyAdvancedFilters} disabled={loading}>
              Aplicar
            </Button>
          </div>
        </div>
      </div>
    )}
  </CardContent>
</Card>


      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>
            Items {loading ? '(cargando...)' : `(${items.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <p className="text-red-600">{error}</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-left w-[10%]">Referencia</TableHead>
                    <TableHead className="text-left w-[10%]">Categoría</TableHead>
                    <TableHead className="text-left w-[10%]">Proveedor</TableHead>
                    <TableHead className="text-left w-[20%]">Descripción</TableHead>
                    <TableHead className="text-center w-[8%]">Precio Tarifa</TableHead>
                    <TableHead className="text-center w-[8%]" title="Desvío comercial (%)">
                      Desv.%
                    </TableHead>
                    <TableHead className="text-center w-[8%]">Costo</TableHead>
                    <TableHead className="text-center w-[8%]" title="Beneficio comercial (%)">
                      Ben.%
                    </TableHead>
                    <TableHead className="text-center w-[8%]">PVP</TableHead>
                    <TableHead className="text-center w-[8%]">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center text-gray-500">
                        Cargando catálogo...
                      </TableCell>
                    </TableRow>
                  ) : (items as CatalogItem[]).length > 0 ? (
                    (items as CatalogItem[]).map((item) => {
                      const desvioComercial = Math.max(Number(item.desvio_comercial_pct ?? 0), 0);
                      const beneficioComercial = Math.max(Number(item.beneficio_comercial_pct ?? 0), 0);
                      return (
                        <TableRow key={item.id} className="hover:bg-gray-50">
                          <TableCell className="text-left w-[10%]"><span className="text-blue-600">{item.ref}</span></TableCell>
                          <TableCell className="text-left text-gray-600 w-[10%]">{item.category}</TableCell>
                          <TableCell className="text-left w-[10%]">{item.supplier}</TableCell>
                          <TableCell className="text-left w-[20%]">
                          {item.description}
                          </TableCell>
                          <TableCell className="text-center text-gray-900 w-[8%]">{item.unit_cost}</TableCell>
                          <TableCell className="text-center w-[8%]">
                            {desvioComercial > 0 ? (
                              <span className="rounded-full bg-blue-100 px-2 py-1 text-sm text-blue-800">
                                {item.desvio_comercial_pct}%
                              </span>
                            ) : (
                              <span className="text-gray-900">{item.desvio_comercial_pct}%</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center text-gray-900 w-[8%]">{item.cost_real}</TableCell>
                          <TableCell className="text-center w-[8%]">
                            {beneficioComercial > 0 ? (
                              <span className="rounded-full bg-green-100 px-2 py-1 text-sm text-green-800">
                                {item.beneficio_comercial_pct}%
                              </span>
                            ) : (
                              <span className="text-gray-900">{item.beneficio_comercial_pct}%</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center text-gray-900 w-[8%]">{item.pvp}</TableCell>
                          <TableCell className="text-center w-[8%]">
                            <div className="flex flex-col items-center gap-2">
                              <Button variant="ghost" size="sm" onClick={() => handleOpenEditDialog(item)}>
                                Editar
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => setConfirmDelete(item)}
                              >
                                Eliminar
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center text-gray-500 py-8">
                        No hay items con los filtros actuales.
                      </TableCell>
                    </TableRow>
                  )}
                  </TableBody>
              </Table>

              <div className="mt-4 flex flex-wrap justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(pagination.page - 1)}
                  disabled={pagination.page <= 1 || loading}
                >
                  Anterior
                </Button>
                <div className="flex items-center gap-2">
                  <Input
                    value={pageInput}
                    onChange={(event) => setPageInput(event.target.value)}
                    onBlur={commitPageInput}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') commitPageInput();
                    }}
                    className="h-9 w-16 text-center"
                    disabled={loading}
                  />
                  <span className="text-sm text-gray-600">/ {totalPages}</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(pagination.page + 1)}
                  disabled={loading || pagination.page >= totalPages}
                >
                  Siguiente
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(totalPages)}
                  disabled={loading || pagination.page >= totalPages}
                >
                  Última página
                </Button>
                <Select
                  value={String(pagination.page_size)}
                  onValueChange={(value) => {
                    const nextSize = Number(value);
                    setPagination((prev: CatalogPagination) =>
                      prev.page_size === nextSize ? prev : { ...prev, page: 1, page_size: nextSize },
                    );
                  }}
                  disabled={loading}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue placeholder={String(pagination.page_size)} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      <CatalogItemDialog
        open={dialogOpen}
        mode={dialogMode}
        initialValues={dialogInitialValues}
        isSubmitting={submitting}
        onSubmit={handleSave}
        onClose={() => setDialogOpen(false)}
        supplierOptions={supplierOptions}
        supplierLoading={supplierLookupLoading}
        onSupplierSearch={handleSupplierSearch}
        categoryOptions={categoryOptions}
        categoryLoading={categoryLookupLoading}
        onCategorySearch={handleCategorySearch}
      />
      <AlertDialog
          open={Boolean(confirmDelete)}
          onOpenChange={(open: boolean) => { if (!open) setConfirmDelete(null); }}
        >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar item?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={submitting}
              onClick={async (event) => {
                event.preventDefault();
                if (!confirmDelete) return;
                setSubmitting(true);
                try {
                  await deleteCatalogItem(confirmDelete.id);
                  toast.success('Item eliminado');
                  await refresh();
                } catch (e) {
                  toast.error('No pudimos eliminar el item');
                } finally {
                  setSubmitting(false);
                  setConfirmDelete(null);
                }
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
